import { useEffect, useState } from 'react'
import { Box, Grid, Skeleton, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material'

import useIsMobile from '../../../../../utils/hooks/useIsMobile'
import useTemperatures from '../../../../../utils/hooks/useTemperatures'
import deviceClasses from '../../index.styles'

import BinaryChart from './binary-chart'
import { getChartChannels, getCharts, getChartTitle } from './helpers'
import classes from './index.styles'
import LineChart from './line-chart'
import LineChartSkeleton from './line-chart-skeleton'
import TabPanel from './tab-panel'

interface IProps {
  device: IDeviceType | null
}

const TrendsSection = ({ device }: IProps) => {
  const { tempUnits } = useTemperatures()
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))
  const isMediumScreen = useMediaQuery(theme.breakpoints.up('md')) && !isLargeScreen
  const isMobile = useIsMobile()
  const [tabValue, setTabValue] = useState(0)
  const currentValues = device?.currentValues || {}
  const nodeType = device?.node?.nodeTypeName || ''

  const charts = getCharts({ currentValues })
  const chartPlots = charts.map((chart) => {
    const channels = getChartChannels({ chart, currentValues, nodeType, tempUnits })
    return {
      chart,
      chartTitle: getChartTitle(chart, currentValues),
      chartType: chart === 'leak' || chart === 'leakprobe' || chart === 'valve' || chart === 'ammonia_warning' ? 'binary' : 'line',
      channels,
    }
  })

  // Reset tab when device changes
  useEffect(() => {
    setTabValue(0)
  }, [device?.node.id])

  return (
    <Grid container={true} size={12} sx={deviceClasses.sectionOuterGrid} className="highcharts-light">
      <Grid container={true} size={12} sx={deviceClasses.sectionTitleGrid}>
        <Typography variant="subtitle1" sx={deviceClasses.sectionTitle}>
          {device ? 'Trends' : <Skeleton width={60} />}
        </Typography>
        <Typography variant="body2">{device ? 'Past week' : <Skeleton width={75} />}</Typography>
      </Grid>

      {!device ? (
        <Grid container={true} size={12} sx={deviceClasses.skeletonGrid}>
          <Skeleton variant="rounded" width="100%" height={48} />
          <Box sx={classes.chartContainer}>
            <LineChartSkeleton />
          </Box>
        </Grid>
      ) : chartPlots.length === 1 ? (
        <Grid container={true} size={12} sx={classes.tabPanel}>
          <Box sx={classes.customTabPanelContent}>
            {chartPlots[0].chartType === 'line' ? (
              <LineChart
                title={chartPlots[0].chartTitle}
                deviceId={device.node.id}
                channels={chartPlots[0].channels}
                alerts={device.alerts}
                chart={chartPlots[0].chart}
                isSignalChart={chartPlots[0].chart === 'signal'}
              />
            ) : (
              <BinaryChart
                title={chartPlots[0].chartTitle}
                deviceId={device.node.id}
                channel={chartPlots[0].channels[0]}
                alerts={device.alerts}
                chart={chartPlots[0].chart}
              />
            )}
          </Box>
        </Grid>
      ) : (
        <>
          <Tabs
            value={tabValue}
            onChange={(_e, newValue) => setTabValue(newValue)}
            variant={isMobile || isMediumScreen ? 'scrollable' : 'fullWidth'}
            sx={classes.tabs}
          >
            {chartPlots.map((plot) => (
              <Tab key={plot.chart} label={plot.chartTitle} sx={classes.tab} />
            ))}
          </Tabs>
          {chartPlots.map((plot, index) => (
            <TabPanel key={plot.chart} value={tabValue} index={index}>
              {plot.chartType === 'line' ? (
                <LineChart
                  title={plot.chartTitle}
                  deviceId={device.node.id}
                  channels={plot.channels}
                  alerts={device.alerts}
                  chart={plot.chart}
                  isSignalChart={plot.chart === 'signal'}
                />
              ) : (
                <BinaryChart title={plot.chartTitle} deviceId={device.node.id} channel={plot.channels[0]} alerts={device.alerts} chart={plot.chart} />
              )}
            </TabPanel>
          ))}
        </>
      )}
    </Grid>
  )
}

export default TrendsSection
